/**
 * Pal Builder — community theorycraft planner.
 *
 * UI/UX mirrors the desktop save-editor "Edit Pals" dialog from
 * chilletsbot — see /tmp/updates/files for updating website/edit_pals.py
 * (3-column layout: Skills | Portrait+Identity | Stats/Souls/Trust).
 *
 * This page is a pure planner. Nothing persists to the DB yet — pick a
 * pal from the Paldex and play with what it _could_ look like.
 *
 * Stat formula (matches in-game Palworld — community-verified by
 * u/blahable & palworld-aio's `calculate_max_*` helpers, with Trust
 * support per the TRUST_PREVIEW_HANDOFF spec):
 *   effBase  = base + friendship_scale × Trust
 *   HP  = (500 + 5×L + L × effHP   × 0.5   × ivMult) × rankM × soulM × passiveM
 *   ATK = (100        + L × effATK × 0.075 × ivMult) × rankM × soulM × passiveM
 *   DEF = (50         + L × effDEF × 0.075 × ivMult) × rankM × soulM × passiveM
 *   ivMult   = 1 + 0.30 × (talent/100)    (talent: 0-100, max bonus +30%)
 *   rankM    = 1 + 0.03 × rank            (condenser stars 0-4)
 *   soulM    = 1 + 0.03 × soul_level      (each soul level = +3%)
 *   passiveM = 1 + Σ(passive_stat_pct)/100
 *
 * Trust is amplified by every downstream multiplier because it goes
 * in BEFORE level scaling — that's why a small Friendship value
 * produces a large delta at L65.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { PageHeader } from "@/components/Atoms";
import { PalPortrait, ElementChip } from "@/components/PalAssets";
import { PalXpPreview } from "@/components/PalXpPreview";
import {
  Search, Star, Heart, Sword, ShieldCheck, Hammer, Sparkles,
  Plus, X, Mars, Venus, Trash2, Zap, RefreshCcw, Server, Lock, Filter,
} from "lucide-react";
import { BuilderSendSection } from "@/components/deliver/SendToGame";
import { WorkSuitGrid } from "@/components/builder/WorkSuitGrid";
import { useBuilderPricing } from "@/lib/useBuilderPricing";
import {
  MAX_ACTIVE, MAX_PASSIVE, MAX_LEVEL, MAX_LEVEL_MODDED,
  MAX_IV, MAX_IV_MODDED, MAX_SOUL, MAX_SOUL_MODDED,
  MAX_RANK, MAX_TRUST,
  MAX_WORK_LEVEL, EMPTY_WORK_SUITS,
  naturalWorkSuitsFromPaldex,
  COMBAT_PRESETS, resolvePresetPassives,
} from "@/lib/builderPresets";

const GENDERS = [
  { value: "female", icon: Venus, colour: "text-rose-400", label: "♀" },
  { value: "male", icon: Mars, colour: "text-sky-400", label: "♂" },
];

// ── page ──────────────────────────────────────────────────────────────
export default function Builder() {
  const toast = useToast();
  const pricing = useBuilderPricing();
  const highLevelSurcharge = Number(pricing.high_level_surcharge) || 2500;

  // Data
  const [pals, setPals] = useState([]);
  const [activeSkills, setActiveSkills] = useState([]);
  const [passiveSkills, setPassiveSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [servers, setServers] = useState([]);

  // VIP status — gates the Modded Demise target. Fetched once on mount.
  // The backend's `/pal/deliver` enforces this server-side too; the UI
  // check is purely so non-VIPs see a friendly lock badge + toast
  // instead of failing at submit time.
  const [isVip, setIsVip] = useState(false);
  const [vipChecked, setVipChecked] = useState(false);

  // Delivery target — picked at the top of the page, drives the
  // level cap (modded → 255, vanilla → 65) and feeds the Send section.
  const [targetServer, setTargetServer] = useState("");
  const targetServerInfo = servers.find((s) => s.name === targetServer) || null;
  const targetIsModded = !!targetServerInfo?.modded;
  // `vip_free` servers (e.g. Chillet's Modded Demise) unlock the full
  // modded range (Lv 1-255, IV 0-255, PalSouls 0-1020) for EVERY user —
  // no VIP required. Backend enforces the same rule in `/pal/deliver`
  // via `_is_vip_free_server`, so this flag is UX-only.
  const targetIsVipFree = !!targetServerInfo?.vip_free;
  // Modded servers unlock PalDefender's extended IV (0-255) and PalSouls
  // (0-1020 per stat) ranges — but only for VIPs, unless the server is
  // flagged `vip_free`. Non-VIPs delivering to a non-vip_free modded
  // server get vanilla-range caps applied.
  const moddedExtendedUnlocked = targetIsModded && (isVip || targetIsVipFree);
  const effectiveLevelCap = moddedExtendedUnlocked ? MAX_LEVEL_MODDED : MAX_LEVEL;
  const effectiveMaxIV = moddedExtendedUnlocked ? MAX_IV_MODDED : MAX_IV;
  const effectiveMaxSoul = moddedExtendedUnlocked ? MAX_SOUL_MODDED : MAX_SOUL;

  // Selection
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);

  // Identity flags
  const [nickname, setNickname] = useState("");
  const [gender, setGender] = useState("female");
  const [isBoss, setIsBoss] = useState(false);
  const [isLucky, setIsLucky] = useState(false);
  const [isAwakened, setIsAwakened] = useState(false);

  // Stats
  const [level, setLevel] = useState(50);
  const [rank, setRank] = useState(0);
  const [ivHp, setIvHp] = useState(50);
  const [ivAtk, setIvAtk] = useState(50);
  const [ivDef, setIvDef] = useState(50);
  const [soulHp, setSoulHp] = useState(0);
  const [soulAtk, setSoulAtk] = useState(0);
  const [soulDef, setSoulDef] = useState(0);
  const [soulCraft, setSoulCraft] = useState(0);
  const [trust, setTrust] = useState(0);
  const [workSuits, setWorkSuits] = useState(EMPTY_WORK_SUITS);

  // Loadout
  const [actives, setActives] = useState([]);   // array of skill objects
  const [passives, setPassives] = useState([]);

  // Datamined exp table — fetched once, used by the XP-to-next preview
  // under the Level slider. Falls back to `null` if the request fails.
  const [expTable, setExpTable] = useState(null);

  // Load everything in parallel
  useEffect(() => {
    Promise.all([
      api.get("/paldex?limit=1000"),
      api.get("/skills/builder/active"),
      api.get("/skills/builder/passive"),
      api.get("/paldex/_meta/exp-table"),
      api.get("/servers"),
    ])
      .then(([px, sk, pv, xp, sv]) => {
        const filtered = (px.data.results || []).filter(
          (p) => !/\(alpha\)/i.test(p.name) && !p.name.includes(" & ")
        );
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        setPals(filtered);
        setActiveSkills(sk.data.skills || []);
        setPassiveSkills(pv.data.skills || []);
        setExpTable(xp.data?.levels || null);
        const srvList = sv.data?.servers || [];
        setServers(srvList);
        if (srvList.length === 1) setTargetServer(srvList[0].name);
      })
      .catch((e) => toast.error(apiError(e, "Couldn't load Builder data")))
      .finally(() => setLoading(false));
    // VIP status — separate, non-fatal. We let the page render even if
    // this 401s (e.g. anonymous user); `isVip` just stays false.
    api.get("/pal/vip-status")
      .then(({ data }) => setIsVip(!!data?.is_vip))
      .catch(() => setIsVip(false))
      .finally(() => setVipChecked(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whenever the user switches to a vanilla server (or away from any
  // server at all), clamp Level / IVs / Souls back down to vanilla-legal
  // ranges. Otherwise a player could max-fill on Modded Demise (Lv 255,
  // IV 255, Soul 1020), switch the target to Revival Pvpve, and submit a
  // request the backend would have to reject — confusing UX. Clamping in
  // the UI keeps the displayed values honest about what'll actually
  // ship in the delivery payload.
  useEffect(() => {
    if (!targetServer || targetIsModded) return;
    if (level > MAX_LEVEL) setLevel(MAX_LEVEL);
    if (ivHp > MAX_IV) setIvHp(MAX_IV);
    if (ivAtk > MAX_IV) setIvAtk(MAX_IV);
    if (ivDef > MAX_IV) setIvDef(MAX_IV);
    if (soulHp > MAX_SOUL) setSoulHp(MAX_SOUL);
    if (soulAtk > MAX_SOUL) setSoulAtk(MAX_SOUL);
    if (soulDef > MAX_SOUL) setSoulDef(MAX_SOUL);
    if (soulCraft > MAX_SOUL) setSoulCraft(MAX_SOUL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetServer, targetIsModded]);

  // Pull full pal data on selection. Also pre-fills the Work Suitability
  // editor with the pal's natural work levels (mapped from the paldex's
  // display-name keys → PalDefender's PalTemplate keys).
  useEffect(() => {
    if (!selected) return;
    setDetails(null);
    api.get(`/paldex/${selected.slug}`)
      .then(({ data }) => {
        setDetails(data);
        setWorkSuits(naturalWorkSuitsFromPaldex(data?.WorkSuitability));
      })
      .catch(() => setDetails({ Stats: {} }));
  }, [selected]);

  // The 12-key dict of "what work skills does this pal natively have"
  // (each entry is the pal's base level, 0 if missing). PalDefender's
  // `ExtraWorkSuitabilities` can ONLY boost work types a pal already
  // possesses — Mining=+5 on a pal without natural Mining is silently
  // dropped by the game engine. So we surface this natural set to the
  // editor: rows for non-native traits are disabled, and Max All /
  // presets only touch keys that will actually take effect in-game.
  const naturalWorkSuits = useMemo(
    () => naturalWorkSuitsFromPaldex(details?.WorkSuitability),
    [details],
  );

  // Live stats — matches the in-game Palworld formula
  // (community-verified — blahable's Reddit research as published on
  // wikily.gg/palworld/stats-calculator/):
  //   HP  = (500 + 5×L + L × (BaseHP + friendship.hp × T) × 0.5 × IVmult) × R × S × P
  //   ATK = (100        + L × (BaseATK + friendship.shot_attack × T) × 0.075 × IVmult) × R × S × P
  //   DEF = (50         + L × (BaseDEF + friendship.defense × T) × 0.075 × IVmult) × R × S × P
  //   WSP =  baseWork × (1 + soulCraft × 0.03) × passiveWorkMult
  // where IVmult = 1 + 0.30 × talent/100  (max +30%),
  //       R     = 1 + 0.05 × rank          (each star = +5%, max +20%),
  //       S     = 1 + 0.03 × soul          (each level = +3%, max +60%),
  //       P     = 1 + Σ(passive_stat_pct)/100,
  //       T     = trust level (0-10). Friendship is null for many
  //               pals (Jetragon, Anubis, all bosses) in which case
  //               Trust contributes nothing.
  //
  // CRITICAL: in-game "Attack" scales from the RANGED value when the
  // pal has one (Jetragon ranged 140 vs melee 100 explains the
  // previously-reported 41% under-shoot on caster pals).
  //
  // NOTE: the in-game "Statue of Power" is the player-character buff
  // structure (it doesn't affect Pal stats — only the human). The
  // soul-condenser system you spend Pal Souls on IS represented by
  // the Souls slider below; there's no separate Statue multiplier
  // to add on top.
  const stats = useMemo(() => {
    if (!details) return null;
    const baseHP = details.Stats?.HP ?? 100;
    // In-game Attack stat scales from the RANGED attack first, with
    // Melee as a fallback for pals that have no ranged value (some
    // pure-melee species). The shape can be a nested object
    // (Attack: {Melee, Ranged}) or a flat number on older data.
    const attackBlock = details.Stats?.Attack;
    const baseAtk =
      (attackBlock && typeof attackBlock === "object"
        ? (attackBlock.Ranged ?? attackBlock.Melee)
        : attackBlock) ?? 80;
    const baseDef = details.Stats?.Defense ?? 50;
    const baseWork = details.Stats?.CraftSpeed ?? 100;
    // Trust scaling values (per-trust-level bonus to the species base).
    // `shot_attack` is the in-game Attack stat — there's no separate
    // melee scalar for Trust.
    const fHp = details.Friendship?.hp ?? 0;
    const fAtk = details.Friendship?.shot_attack ?? 0;
    const fDef = details.Friendship?.defense ?? 0;
    const rankMult = 1 + rank * 0.05;
    const passiveBoost = sumPassiveBoosts(passives);
    const ivMult = (talent) => 1 + 0.30 * (talent / 100);
    const compute = (kind, base, fScale, talent, soulLvl, passiveKey, trustLvl) => {
      const soulMult = 1 + soulLvl * 0.03;
      const passiveMult = 1 + (passiveBoost[passiveKey] || 0) / 100;
      const effBase = base + fScale * trustLvl;
      let raw;
      if (kind === "hp") {
        raw = 500 + 5 * level + level * effBase * 0.5 * ivMult(talent);
      } else {
        const floor = kind === "atk" ? 100 : 50;
        raw = floor + level * effBase * 0.075 * ivMult(talent);
      }
      return Math.floor(raw * rankMult * soulMult * passiveMult);
    };
    const hp = compute("hp", baseHP, fHp, ivHp, soulHp, "hp", trust);
    const atk = compute("atk", baseAtk, fAtk, ivAtk, soulAtk, "atk", trust);
    const def = compute("def", baseDef, fDef, ivDef, soulDef, "def", trust);
    // Work Speed — distinct from HP/ATK/DEF: no level scaling, no
    // condenser. Just the pal's CraftSpeed × soul-craft multiplier ×
    // passive percentage. Negative passive percentages (Musclehead
    // -50% craft) still apply.
    const workPassiveMult = 1 + (passiveBoost.work || 0) / 100;
    const workSoulMult = 1 + soulCraft * 0.03;
    const work = Math.max(0, Math.floor(baseWork * workSoulMult * workPassiveMult));
    // Re-run with trust=0 so we can show "(+X from Trust)" in the UI
    const hp0 = compute("hp", baseHP, fHp, ivHp, soulHp, "hp", 0);
    const atk0 = compute("atk", baseAtk, fAtk, ivAtk, soulAtk, "atk", 0);
    const def0 = compute("def", baseDef, fDef, ivDef, soulDef, "def", 0);
    return {
      baseHP, baseAtk, baseDef, baseWork,
      hp, atk, def, work,
      trustDelta: { hp: hp - hp0, atk: atk - atk0, def: def - def0 },
      hasFriendship: !!details.Friendship,
      passiveBoost,
    };
  }, [details, level, rank, ivHp, ivAtk, ivDef, soulHp, soulAtk, soulDef, soulCraft, passives, trust]);

  // Actions — quick-fill respects whichever IV / Soul ceiling the
  // selected delivery target unlocks (modded servers go to 255 / 1020).
  const maxIVs = () => { setIvHp(effectiveMaxIV); setIvAtk(effectiveMaxIV); setIvDef(effectiveMaxIV); };
  const maxSouls = () => { setSoulHp(effectiveMaxSoul); setSoulAtk(effectiveMaxSoul); setSoulDef(effectiveMaxSoul); setSoulCraft(effectiveMaxSoul); };
  const maxWorkSuits = () => setWorkSuits(prev => {
    // Only boost work types this pal has natively — PalDefender's
    // ExtraWorkSuitabilities silently drops non-native keys in-game.
    const out = { ...prev };
    for (const k of Object.keys(out)) {
      if ((naturalWorkSuits[k] || 0) > 0) out[k] = MAX_WORK_LEVEL;
    }
    return out;
  });
  const resetWorkSuits = () => setWorkSuits(naturalWorkSuits);
  const setWorkSuit = (key, value) => {
    setWorkSuits((prev) => ({
      ...prev,
      [key]: Math.max(0, Math.min(MAX_WORK_LEVEL, Number(value) || 0)),
    }));
  };
  const maxAll = () => {
    // Use the per-target effective cap so Modded Demise pushes to Lv 255
    // (and IV 255 / Soul 1020 via `maxIVs`/`maxSouls` which already
    // respect `effectiveMaxIV`/`effectiveMaxSoul`). Vanilla servers and
    // the "no target picked" state stay at the Lv 65 ceiling.
    setLevel(effectiveLevelCap); setRank(MAX_RANK);
    maxIVs(); maxSouls(); setTrust(MAX_TRUST); maxWorkSuits();
  };
  const reset = () => {
    setLevel(50); setRank(0);
    setIvHp(50); setIvAtk(50); setIvDef(50);
    setSoulHp(0); setSoulAtk(0); setSoulDef(0); setSoulCraft(0);
    setTrust(0); resetWorkSuits();
    setActives([]); setPassives([]); setNickname("");
    setIsBoss(false); setIsLucky(false); setGender("female");
  };
  const addActive = (s) => {
    if (actives.length >= MAX_ACTIVE) {
      toast.info(`Max ${MAX_ACTIVE} active skills`); return;
    }
    if (actives.some((a) => a.name === s.name)) return;
    setActives([...actives, s]);
  };
  const addPassive = (s) => {
    if (passives.length >= MAX_PASSIVE) {
      toast.info(`Max ${MAX_PASSIVE} passives`); return;
    }
    if (passives.some((a) => a.name === s.name)) return;
    setPassives([...passives, s]);
    // Picking the "Lucky" passive grants ONLY the +15 % stat boost.
    // The shimmer/✨ icon-by-name is a separate cosmetic flag that
    // requires the user to also click the ✨ Lucky button below
    // (Palworld in-game proof: 2026-02-22 — the player explicitly
    // needs Shiny=true to see the icon by the pal name; the Lucky
    // passive alone does NOT light the icon up).
    // We also intentionally do NOT touch isBoss here — the Boss vs
    // Lucky mutex is owned by the toggle buttons, not the passive
    // list, so a user can carry the Lucky passive on an alpha pal.
  };

  // ✨ Lucky button = shimmer-only toggle (`Shiny: true` in the
  // template). Palworld only displays ONE cosmetic icon next to a
  // pal's name — Lucky takes precedence over Alpha visually
  // (verified in-game 2026-02-22: a pal with both Shiny=true AND
  // IsBoss=true rendered with only the ✨ icon, never the 👑).
  // So we make Lucky and Boss mutually exclusive at the UI level —
  // toggling one OFF the other so the user can't waste coins on a
  // combo that won't render. This does NOT affect the Lucky passive
  // in the passives list (the picker controls that independently).
  const toggleLucky = () => {
    setIsLucky((v) => {
      const next = !v;
      if (next) setIsBoss(false);  // mutex: enabling Lucky → kill Boss
      return next;
    });
  };
  const toggleBoss = () => {
    setIsBoss((v) => {
      const next = !v;
      if (next) setIsLucky(false); // mutex: enabling Boss → kill Lucky
      return next;
    });
  };
  // Awakened (PalDefender `IsAwakening`) — independent of Boss/Lucky;
  // the in-game awakened aura renders alongside either icon.
  const toggleAwakened = () => setIsAwakened((v) => !v);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 fade-up" data-testid="builder-page">
      <PageHeader
        title="Pal Builder"
        badge="Beta"
        subtitle="Theorycraft what a pal could look like — match a real in-game save with talents, souls, and skills."
        right={
          selected && (
            <div className="flex gap-2">
              <button onClick={reset} className="btn btn-ghost text-xs" data-testid="builder-reset">
                <RefreshCcw size={12} /> Reset
              </button>
              <button onClick={maxAll} className="btn btn-primary text-xs" data-testid="builder-max-all">
                <Zap size={12} /> Max All
              </button>
            </div>
          )
        }
      />

      {/* Delivery Target — picked first because it gates the level cap
          (modded servers unlock Lv 80+). Always visible above the pal
          picker so the user knows where their build will land. */}
      <DeliveryTargetPicker
        servers={servers}
        value={targetServer}
        onChange={(name) => {
          setTargetServer(name);
          // Friendly heads-up for non-VIPs picking a modded server —
          // they can still build, but they'll be clamped to vanilla
          // caps until they unlock VIP. Suppressed for `vip_free`
          // modded servers (extended range is open to everyone there).
          const picked = servers.find((s) => s.name === name);
          if (picked?.modded && !isVip && !picked?.vip_free) {
            toast.info(
              `${name}: non-VIP builds are vanilla-capped (Lv 80 / IV 100 / Soul 20). Use \`/vip\` in Discord to unlock Lv 255 / IV 255 / Soul 1020.`
            );
          }
        }}
        isModded={targetIsModded}
        isVip={isVip}
      />

      {/* Pal picker */}
      <div className="glass-card p-4 mb-6">
        <h3 className="label mb-2">Pick a Pal</h3>
        <PalPicker pals={pals} loading={loading} value={selected} onChange={setSelected} />
      </div>

      {/* Main 3-column editor — only after a pal is chosen */}
      {selected ? (
        <div className="grid lg:grid-cols-12 gap-6" data-testid="builder-editor">
          {/* LEFT — Skills */}
          <div className="lg:col-span-3 space-y-4">
            <SkillsPanel
              title="Active Skills"
              max={MAX_ACTIVE}
              selected={actives}
              setSelected={setActives}
              onAdd={addActive}
              library={activeSkills}
              kind="active"
            />
            <SkillsPanel
              title="Passive Skills"
              max={MAX_PASSIVE}
              selected={passives}
              setSelected={setPassives}
              onAdd={addPassive}
              library={passiveSkills}
              kind="passive"
            />

            {/* Combat-loadout one-click presets — every passive used
                here is universal (no species lock), so any pal can roll
                them naturally via breeding/passive transfer. */}
            <div className="glass-card p-3" data-testid="builder-combat-presets">
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] mb-2 flex items-center gap-1.5">
                <Sparkles size={11} className="text-fuchsia-300" /> Loadout Presets
              </div>
              <div className="flex flex-wrap gap-1.5">
                {COMBAT_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => {
                      const resolved = resolvePresetPassives(p.id, passiveSkills);
                      if (resolved.length === 0) {
                        toast.info("Passive library still loading…");
                        return;
                      }
                      setPassives(resolved);
                      // Presets only fill the passive list — they do NOT
                      // touch the cosmetic ✨/👑 icons. The user owns
                      // those via the toggle buttons below (Palworld
                      // only shows one icon at a time, so we keep the
                      // mutex strictly UI-driven).
                    }}
                    className="text-[10px] px-2 py-1 rounded-md border border-white/10 hover:border-fuchsia-400/40 hover:bg-fuchsia-500/5 transition-colors"
                    data-testid={`builder-combat-preset-${p.id}`}
                    title={p.assets.join(", ")}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <p className="text-[9px] text-[var(--muted)] mt-2 leading-snug">
                Each preset uses 4 universal passives that any pal can roll.
              </p>
            </div>
          </div>

          {/* CENTER — Portrait + identity */}
          <div className="lg:col-span-4">
            <CenterIdentity
              pal={selected}
              details={details}
              nickname={nickname}
              setNickname={setNickname}
              gender={gender}
              setGender={setGender}
              isBoss={isBoss}
              toggleBoss={toggleBoss}
              isLucky={isLucky}
              toggleLucky={toggleLucky}
              isAwakened={isAwakened}
              toggleAwakened={toggleAwakened}
            />

            {/* Live stats card */}
            {stats && (
              <div className="glass-card p-4 mt-4" data-testid="builder-stats">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <StatTile Icon={Heart} label="HP" value={stats.hp} base={stats.baseHP}
                            trustDelta={stats.trustDelta.hp}
                            colour="text-emerald-300" testId="stat-hp" />
                  <StatTile Icon={Sword} label="ATK" value={stats.atk} base={stats.baseAtk}
                            trustDelta={stats.trustDelta.atk}
                            colour="text-rose-300" testId="stat-atk" />
                  <StatTile Icon={ShieldCheck} label="DEF" value={stats.def} base={stats.baseDef}
                            trustDelta={stats.trustDelta.def}
                            colour="text-sky-300" testId="stat-def" />
                  <StatTile Icon={Hammer} label="WORK" value={stats.work} base={stats.baseWork}
                            colour="text-amber-300" testId="stat-work" />
                </div>
                <p className="text-[10px] text-[var(--muted)] mt-3 leading-relaxed">
                  In-game formula · IV is the 0–100 <em>talent</em> number (max = +30%).<br/>
                  <code className="text-[var(--bone)] text-[9px]">
                    HP = (500 + 5×L + L × baseHP × 0.5 × ivMult) × rank × soul × passive
                  </code><br/>
                  ATK scales from Ranged when present (Jetragon: 140), otherwise Melee.
                </p>
              </div>
            )}
          </div>

          {/* RIGHT — Stats / Talents / Souls / Trust */}
          <div className="lg:col-span-5 space-y-4">
            <RightStats
              level={level} setLevel={setLevel}
              rank={rank} setRank={setRank}
              ivHp={ivHp} setIvHp={setIvHp}
              ivAtk={ivAtk} setIvAtk={setIvAtk}
              ivDef={ivDef} setIvDef={setIvDef}
              soulHp={soulHp} setSoulHp={setSoulHp}
              soulAtk={soulAtk} setSoulAtk={setSoulAtk}
              soulDef={soulDef} setSoulDef={setSoulDef}
              soulCraft={soulCraft} setSoulCraft={setSoulCraft}
              trust={trust} setTrust={setTrust}
              workSuits={workSuits}
              setWorkSuit={setWorkSuit}
              setWorkSuits={setWorkSuits}
              naturalWorkSuits={naturalWorkSuits}
              levelCap={effectiveLevelCap}
              targetIsModded={targetIsModded}
              hasTargetServer={!!targetServer}
              highLevelSurcharge={highLevelSurcharge}
              maxIV={effectiveMaxIV}
              maxSoul={effectiveMaxSoul}
              onMaxIVs={maxIVs}
              onMaxSouls={maxSouls}
              onMaxTrust={() => setTrust(MAX_TRUST)}
              onMaxWorkSuits={maxWorkSuits}
              onResetWorkSuits={resetWorkSuits}
              expTable={expTable}
            />
          </div>
        </div>
      ) : (
        <div className="glass-card p-12 text-center text-[var(--muted)] text-sm">
          <Sparkles size={24} className="mx-auto mb-3 opacity-60" />
          Pick a pal above to start building.
        </div>
      )}

      {/* Send-to-game CTA — appears below the form when a pal is picked.
          Replaces the standalone /deliver page per 2026-06-13 consolidation. */}
      <BuilderSendSection
        pal={selected}
        level={level}
        ivs={{ hp: ivHp, atk: ivAtk, def: ivDef }}
        souls={{ hp: soulHp, atk: soulAtk, def: soulDef, craft: soulCraft }}
        passives={passives}
        actives={actives}
        isLucky={isLucky}
        isBoss={isBoss}
        isAwakened={isAwakened}
        nickname={nickname}
        rank={rank}
        trust={trust}
        workSuits={workSuits}
        server={targetServer}
        servers={servers}
      />
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────
function sumPassiveBoosts(passives) {
  const out = { hp: 0, atk: 0, def: 0, work: 0 };
  for (const p of passives || []) {
    for (const e of p.effects || []) {
      const stat = (e.stat || "").toUpperCase();
      const v = parseInt(String(e.value || "").replace(/[^-\d]/g, ""), 10);
      if (Number.isNaN(v)) continue;
      if (stat.includes("DEFENSE")) out.def += v;
      else if (stat.includes("MELEE") || stat.includes("SHOT ATTACK") || stat === "ATTACK") out.atk += v;
      else if (stat.includes("HP") || stat.includes("HEALTH")) out.hp += v;
      // CraftSpeed/Work Speed bonuses (e.g., Musclehead −50% Craft
      // Speed, Artisan +50% Work Speed). The bot's passive table
      // uses "CRAFT SPEED"/"WORK SPEED" interchangeably with the
      // in-game UI's "Work Speed" — match both spellings.
      else if (stat.includes("CRAFT") || stat.includes("WORK SPEED")) out.work += v;
    }
  }
  return out;
}

// ── components ────────────────────────────────────────────────────────
/**
 * Delivery Target card — sits at the very top of the Pal Builder so the
 * user picks WHERE the pal will land before building stats. Modded
 * targets unlock the Lv 255 ceiling; vanilla stays at 65.
 */
function DeliveryTargetPicker({ servers, value, onChange, isModded, isVip }) {
  if ((servers || []).length === 0) {
    return (
      <div
        className="glass-card p-4 mb-6 text-xs text-[var(--muted)]"
        data-testid="builder-target-empty"
      >
        Loading delivery targets…
      </div>
    );
  }
  return (
    <section
      className="glass-card p-4 mb-6 fade-up"
      data-testid="builder-target-section"
    >
      <header className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <span className="label flex items-center gap-1.5">
          <Server size={12} className="text-[var(--teal)]" /> Delivery Target
        </span>
        {value && (
          <span
            className={
              "text-[10px] font-mono px-2 py-0.5 rounded-full border " +
              (isModded
                ? "border-amber-400/40 bg-amber-500/10 text-amber-300"
                : "border-white/10 bg-white/[0.03] text-[var(--muted)]")
            }
            data-testid="builder-target-cap-badge"
          >
            {isModded ? "MODDED · Lv 1–255" : "Vanilla · Lv 1–80"}
          </span>
        )}
      </header>
      <div className="grid sm:grid-cols-2 gap-2">
        {servers.map((s) => {
          const active = value === s.name;
          // Modded targets accept deliveries from everyone; non-VIPs
          // just get vanilla-range caps applied. Render a small hint
          // badge so non-VIPs see the upgrade path without being
          // blocked from clicking. `vip_free` modded servers unlock
          // the extended range for everyone — hide the VIP hint.
          const vipPerk = !!s.modded && !isVip && !s.vip_free;
          const openModded = !!s.modded && !!s.vip_free;
          return (
            <button
              key={s.name}
              type="button"
              onClick={() => onChange(s.name)}
              data-testid={`builder-target-${s.name}`}
              title={vipPerk
                ? "VIP unlocks the extended Lv 255 / IV 255 / Soul 1020 ranges. Non-VIPs deliver here at vanilla caps."
                : undefined}
              className={
                "p-3 rounded-md border text-left transition-colors flex items-center justify-between gap-2 " +
                (active
                  ? "border-[var(--teal)] bg-[rgba(0,229,255,0.07)] text-[var(--teal)]"
                  : "border-white/10 hover:border-[var(--teal-soft)]")
              }
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{s.name}</div>
                <div className="text-[10px] text-[var(--muted)] truncate">{s.host}</div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {s.modded && (
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-amber-400/40 bg-amber-500/10 text-amber-300">
                    MOD
                  </span>
                )}
                {openModded && (
                  <span
                    className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-emerald-400/40 bg-emerald-500/10 text-emerald-300"
                    aria-label="Full modded range open to everyone (no VIP required)"
                    title="Full modded range (Lv 255 / IV 255 / Soul 1020) open to everyone — no VIP required."
                    data-testid={`builder-target-${s.name}-open-badge`}
                  >
                    OPEN
                  </span>
                )}
                {vipPerk && (
                  <span
                    className="text-[9px] font-mono px-1.5 py-0.5 rounded border border-purple-400/40 bg-purple-500/10 text-purple-300 inline-flex items-center gap-0.5"
                    aria-label="VIP unlocks higher caps"
                  >
                    <Lock size={9} /> VIP
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}


// Palworld element list used by the Pal Builder + Breeding pickers.
// Lower-case identifiers match the way the /paldex API surfaces
// `elements` when normalized in filters. Keep in sync with the same
// list in Breeding.jsx and PalAssets.jsx.
const ELEMENTS = ["dark", "dragon", "electric", "fire", "grass", "ground", "ice", "neutral", "water"];


function PalPicker({ pals, loading, value, onChange }) {
  const [q, setQ] = useState("");
  const [elementFilter, setElementFilter] = useState(null);
  const filtered = useMemo(() => {
    let list = pals;
    if (elementFilter) {
      list = list.filter((p) =>
        (p.elements || []).some((e) => e.toLowerCase() === elementFilter)
      );
    }
    const n = q.trim().toLowerCase();
    if (!n) return list;
    return list.filter((p) =>
      p.name.toLowerCase().includes(n) ||
      (p.elements || []).some((e) => e.toLowerCase().includes(n))
    );
  }, [pals, q, elementFilter]);
  return (
    <div>
      <div className="relative mb-3">
        <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
        <input
          className="input pl-8"
          placeholder={loading ? "Loading Paldex…" : "Search pals…"}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          disabled={loading}
          data-testid="builder-pal-search"
        />
      </div>

      {/* Element filter chips — narrow the picker grid by element.
          Includes Neutral so pals like Cattiva, Direhowl, Tocotoco
          are one click away instead of buried in the 400-pal grid. */}
      <div className="flex items-center gap-1.5 flex-wrap mb-2" data-testid="builder-pal-element-filters">
        <Filter size={11} className="text-[var(--muted)]" />
        <button
          onClick={() => setElementFilter(null)}
          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
            elementFilter === null
              ? "border-[var(--teal)] text-[var(--teal)] bg-[rgba(0,229,255,0.06)]"
              : "border-white/10 text-[var(--muted)] hover:text-[var(--bone)]"
          }`}
          data-testid="builder-pal-element-all"
        >
          All
        </button>
        {ELEMENTS.map((el) => {
          const active = elementFilter === el;
          return (
            <button
              key={el}
              onClick={() => setElementFilter(active ? null : el)}
              className={`p-0.5 rounded-full border transition-colors ${
                active
                  ? "border-[var(--teal)] bg-[rgba(0,229,255,0.06)]"
                  : "border-white/10 hover:bg-white/5 opacity-70 hover:opacity-100"
              }`}
              title={el}
              data-testid={`builder-pal-element-${el}`}
            >
              <ElementChip element={el} size={14} showLabel={false} />
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-12 lg:grid-cols-16 gap-2 max-h-[180px] overflow-y-auto pr-1">
        {filtered.slice(0, 400).map((p) => {
          const active = value?.slug === p.slug;
          const portrait = p.image && p.image.startsWith("http")
            ? p.image
            : p.image
              ? `${process.env.REACT_APP_BACKEND_URL}${p.image}`
              : null;
          return (
            <button
              key={p.slug}
              onClick={() => onChange(p)}
              className={`rounded-md transition-colors border ${
                active ? "border-[var(--teal)] bg-[rgba(0,229,255,0.12)] ring-1 ring-[var(--teal)]/40"
                       : "border-white/5 hover:bg-white/5"
              }`}
              data-testid={`builder-pal-${p.slug}`}
              title={p.name}
            >
              <div className="w-full aspect-square bg-black/30 flex items-center justify-center overflow-hidden rounded-md">
                {portrait ? (
                  <img src={portrait} alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <PalPortrait name={p.name} size="w-full h-full" />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CenterIdentity({
  pal, details,
  nickname, setNickname,
  gender, setGender,
  isBoss, toggleBoss,
  isLucky, toggleLucky,
  isAwakened, toggleAwakened,
}) {
  const portrait = pal.image && pal.image.startsWith("http")
    ? pal.image
    : pal.image
      ? `${process.env.REACT_APP_BACKEND_URL}${pal.image}`
      : null;
  const GenderIcon = (GENDERS.find((g) => g.value === gender) || GENDERS[0]).icon;
  const genderColour = (GENDERS.find((g) => g.value === gender) || GENDERS[0]).colour;
  return (
    <div className="glass-card p-4">
      {/* Portrait */}
      <div className="relative mx-auto w-56 h-56 rounded-xl overflow-hidden bg-black/40 border border-white/10"
           data-testid="builder-portrait">
        {portrait ? (
          <img src={portrait} alt={pal.name} className="w-full h-full object-cover"
               style={{ filter: isLucky ? "drop-shadow(0 0 12px rgba(255,196,90,0.6)) saturate(1.35)" : undefined }} />
        ) : (
          <PalPortrait name={pal.name} size="w-full h-full" />
        )}
        {isBoss && (
          <img
            src="/icons/alpha_emoji.webp"
            alt="Boss"
            className="absolute top-2 left-2 w-9 h-9 object-contain drop-shadow-[0_0_6px_rgba(239,68,68,0.7)] pointer-events-none"
            data-testid="builder-portrait-boss-badge"
          />
        )}
        {isLucky && (
          <div className="absolute top-2 right-2 px-2 py-0.5 rounded bg-yellow-900/80 text-yellow-200 text-[10px] uppercase tracking-wider border border-yellow-400/40">
            ✨ LUCKY
          </div>
        )}
        {isAwakened && (
          <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-purple-900/80 text-purple-200 text-[10px] uppercase tracking-wider border border-purple-400/40"
               data-testid="builder-portrait-awakened-badge">
            ⚡ AWAKENED
          </div>
        )}
        <div className="absolute bottom-2 left-2 flex items-center gap-2">
          {(pal.elements || []).map((e) => (
            <ElementChip key={e} element={e} size={14} showLabel={false} />
          ))}
        </div>
      </div>

      {/* Name + nickname */}
      <div className="mt-3 text-center">
        <h2 className="text-2xl truncate" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
          {pal.name}
        </h2>
        <input
          className="input mt-2 text-center text-sm"
          placeholder="Nickname (optional)…"
          value={nickname}
          onChange={(e) => setNickname(e.target.value.slice(0, 24))}
          data-testid="builder-nickname"
          maxLength={24}
        />
      </div>

      {/* Toggles — flex-wrap so the 4 buttons stack into extra rows instead
          of overflowing the card into the neighbouring panels. */}
      <div className="flex flex-wrap justify-center gap-2 mt-3">
        <ToggleBtn active={isBoss} onClick={toggleBoss}
                   imageSrc="/icons/alpha_emoji.webp" label="Boss" colour="text-red-300"
                   testId="builder-boss-toggle"
                   title="Spawns the alpha/boss character variant with the 👑 crown by its name (Shiny=false, IsBoss=true). Mutually exclusive with ✨ Lucky — Palworld only shows one icon at a time." />
        <ToggleBtn active={isLucky} onClick={toggleLucky}
                   icon={Sparkles} label="Lucky" colour="text-yellow-300"
                   testId="builder-lucky-toggle"
                   title="Shimmer + ✨ Lucky tag only (Shiny=true). To also get the +15% Lucky passive stat boost, pick it from the Passives list. Mutually exclusive with 👑 Boss." />
        <ToggleBtn active={isAwakened} onClick={toggleAwakened}
                   icon={Zap} label="Awakened" colour="text-purple-300"
                   testId="builder-awakened-toggle"
                   title="Delivers the pal in its Awakened state (PalDefender IsAwakening=true — the 1.0 awakening aura). Stacks with Boss or Lucky." />
        <button
          onClick={() => setGender(gender === "female" ? "male" : "female")}
          className="px-3 py-1.5 rounded-md border border-white/10 hover:border-white/30 text-xs uppercase tracking-wider flex items-center gap-1.5"
          data-testid="builder-gender-toggle"
          title="Toggle gender"
        >
          <GenderIcon size={14} className={genderColour} />
          <span className={genderColour}>{gender === "female" ? "Female" : "Male"}</span>
        </button>
      </div>

      {/* Partner skill (read-only) */}
      {(details?.PartnerSkill || details?.partner_skill) && (
        <PartnerSkillBlock skill={details.PartnerSkill || details.partner_skill} />
      )}

      <div className="text-center mt-3">
        <Link to={`/paldex/${pal.slug}`}
              className="text-[10px] text-[var(--teal)] hover:underline uppercase tracking-wider">
          Open full Paldex entry →
        </Link>
      </div>
    </div>
  );
}

function ToggleBtn({ active, onClick, icon: Icon, imageSrc, label, colour, testId, title }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`px-3 py-1.5 rounded-md border text-xs uppercase tracking-wider flex items-center gap-1.5 transition-colors ${
        active
          ? "border-[var(--teal)] bg-[rgba(0,229,255,0.10)]"
          : "border-white/10 hover:border-white/30"
      }`}
      data-testid={testId}
    >
      {imageSrc ? (
        <img
          src={imageSrc}
          alt=""
          className={`w-4 h-4 object-contain transition-opacity ${active ? "opacity-100" : "opacity-40"}`}
        />
      ) : (
        <Icon size={14} className={active ? colour : "text-white/40"} />
      )}
      <span className={active ? "" : "text-white/50"}>{label}</span>
    </button>
  );
}

// XP preview component is now shared with the Pal Box detail modal —
// see /app/frontend/src/components/PalXpPreview.jsx for the canonical
// implementation. The Builder still pre-fetches the table in its
// parallel Promise.all and passes it in via prop so the slider feels
// instant (no per-move fetch).

function RightStats({
  level, setLevel,
  rank, setRank,
  ivHp, setIvHp, ivAtk, setIvAtk, ivDef, setIvDef,
  soulHp, setSoulHp, soulAtk, setSoulAtk, soulDef, setSoulDef, soulCraft, setSoulCraft,
  trust, setTrust,
  workSuits, setWorkSuit, setWorkSuits,
  naturalWorkSuits,
  levelCap, targetIsModded, hasTargetServer, highLevelSurcharge,
  maxIV, maxSoul,
  onMaxIVs, onMaxSouls, onMaxTrust, onMaxWorkSuits, onResetWorkSuits,
  expTable,
}) {
  return (
    <>
      {/* Level + Rank */}
      <div className="glass-card p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="label">Level</span>
          <span className="font-mono text-sm">{level} / {levelCap}</span>
        </div>
        <input type="range" min={1} max={levelCap} value={level}
               onChange={(e) => setLevel(Number(e.target.value))}
               className="w-full accent-[var(--teal)]"
               data-testid="builder-level-input" />
        <PalXpPreview level={level} maxLevel={levelCap} expTable={expTable} testid="builder-xp-preview" />
        {!hasTargetServer && (
          <p className="text-[10px] text-[var(--muted)] mt-1 leading-snug"
             data-testid="builder-level-pick-server-note">
            Pick a delivery target above to unlock the modded Lv 255 ceiling.
          </p>
        )}
        {hasTargetServer && targetIsModded && level > MAX_LEVEL && (
          <p className="text-[10px] text-amber-300 mt-1 leading-snug"
             data-testid="builder-level-modded-note">
            Modded build · adds a flat <span className="font-semibold">+{highLevelSurcharge.toLocaleString()}</span> coin surcharge on delivery.
          </p>
        )}

        <div className="mt-4 flex items-center justify-between">
          <span className="label">Rank (Condenser, +5%/star)</span>
          <RankStars value={rank} onChange={setRank} />
        </div>
      </div>

      {/* Talents */}
      <div className="glass-card p-4" data-testid="builder-talents">
        <header className="flex items-center justify-between mb-1">
          <span className="label">Talents (IVs)</span>
          <button onClick={onMaxIVs} className="btn btn-ghost text-xs"
                  data-testid="builder-max-ivs">Max IVs</button>
        </header>
        <p className="text-[10px] text-[var(--muted)] mb-3">
          {targetIsModded
            ? <>Raw 0–<span className="text-amber-300">{maxIV}</span> talent value (modded). 100 = +30% stat scaling; values above 100 over-scale stats per PalDefender&apos;s extended range.</>
            : <>Raw 0–100 talent value. 100 = +30% stat scaling.</>}
        </p>
        <NumberRow label="HP" Icon={Heart} colour="text-emerald-300" max={maxIV}
                   value={ivHp} onChange={setIvHp} testId="builder-iv-hp" />
        <NumberRow label="Attack" Icon={Sword} colour="text-rose-300" max={maxIV}
                   value={ivAtk} onChange={setIvAtk} testId="builder-iv-atk" />
        <NumberRow label="Defense" Icon={ShieldCheck} colour="text-sky-300" max={maxIV}
                   value={ivDef} onChange={setIvDef} testId="builder-iv-def" />
      </div>

      {/* Souls */}
      <div className="glass-card p-4" data-testid="builder-souls">
        <header className="flex items-center justify-between mb-3">
          <span className="label">
            Souls {targetIsModded ? <span className="text-amber-300 text-[10px]">(modded · 0–{maxSoul})</span> : <span>(small/med/large/king)</span>}
          </span>
          <button onClick={onMaxSouls} className="btn btn-ghost text-xs"
                  data-testid="builder-max-souls">Max Souls</button>
        </header>
        <NumberRow label="HP" Icon={Heart} colour="text-emerald-300" max={maxSoul} suffix={targetIsModded ? "" : "%"}
                   value={soulHp} onChange={setSoulHp} testId="builder-soul-hp" />
        <NumberRow label="Attack" Icon={Sword} colour="text-rose-300" max={maxSoul} suffix={targetIsModded ? "" : "%"}
                   value={soulAtk} onChange={setSoulAtk} testId="builder-soul-atk" />
        <NumberRow label="Defense" Icon={ShieldCheck} colour="text-sky-300" max={maxSoul} suffix={targetIsModded ? "" : "%"}
                   value={soulDef} onChange={setSoulDef} testId="builder-soul-def" />
        <NumberRow label="Craft Speed" Icon={Hammer} colour="text-amber-300" max={maxSoul} suffix={targetIsModded ? "" : "%"}
                   value={soulCraft} onChange={setSoulCraft} testId="builder-soul-craft" />
      </div>

      {/* Trust */}
      <div className="glass-card p-4" data-testid="builder-trust">
        <header className="flex items-center justify-between mb-2">
          <span className="label flex items-center gap-1.5">
            <Heart size={12} className="text-pink-400 fill-pink-400/40" /> Trust
          </span>
          <button onClick={onMaxTrust} className="btn btn-ghost text-xs"
                  data-testid="builder-max-trust">Max Trust</button>
        </header>
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="font-mono">Lv. {trust}</span>
          <span className="text-[var(--muted)]">{trust * 100} pts</span>
        </div>
        <div className="h-2 rounded-full bg-white/5 overflow-hidden mb-2">
          <div className="h-full bg-gradient-to-r from-pink-500 to-rose-300"
               style={{ width: `${(trust / MAX_TRUST) * 100}%` }} />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setTrust(Math.max(0, trust - 1))}
                  className="btn btn-ghost text-xs px-3"
                  data-testid="builder-trust-down">−</button>
          <input type="range" min={0} max={MAX_TRUST} value={trust}
                 onChange={(e) => setTrust(Number(e.target.value))}
                 className="flex-1 accent-pink-400"
                 data-testid="builder-trust-input" />
          <button onClick={() => setTrust(Math.min(MAX_TRUST, trust + 1))}
                  className="btn btn-ghost text-xs px-3"
                  data-testid="builder-trust-up">+</button>
        </div>
        <p className="text-[10px] text-[var(--muted)] mt-2 leading-snug"
           data-testid="builder-trust-note">
          Delivered as <span className="font-mono">FriendshipPoints</span> — Trust 10 ships with the full 200,000-pt cap so the pal arrives bonded and the stat bonuses are live in-game.
        </p>
      </div>

      {/* Work Suitability — base-camp work levels. Extracted to its own
          component so this page stays readable. */}
      <WorkSuitGrid
        workSuits={workSuits}
        setWorkSuit={setWorkSuit}
        setWorkSuits={setWorkSuits}
        naturalWorkSuits={naturalWorkSuits}
        onMaxWorkSuits={onMaxWorkSuits}
        onResetWorkSuits={onResetWorkSuits}
      />
    </>
  );
}


function NumberRow({ label, Icon, colour, value, onChange, max, suffix = "", testId }) {
  return (
    <div className="flex items-center gap-3 py-1" data-testid={testId}>
      <Icon size={13} className={`${colour} flex-shrink-0`} />
      <span className="text-xs w-20 flex-shrink-0">{label}</span>
      <input type="range" min={0} max={max} value={value}
             onChange={(e) => onChange(Number(e.target.value))}
             className={`flex-1 min-w-0 accent-current ${colour}`} />
      <input type="number" min={0} max={max} value={value}
             onChange={(e) => {
               const v = Number(e.target.value);
               if (Number.isFinite(v)) onChange(Math.max(0, Math.min(max, v)));
             }}
             // Inline width pins the box — the global `.input` class is
             // width:100% and beats Tailwind's w-16, which let these boxes
             // balloon and crush the sliders ("IVs/souls messed up" bug).
             style={{ width: max >= 1000 ? 72 : 60, flex: "0 0 auto" }}
             className="input no-spin text-sm font-mono text-center px-1 py-1.5"
             data-testid={`${testId}-input`} />
      {suffix && <span className="text-xs text-[var(--muted)] w-3">{suffix}</span>}
    </div>
  );
}

function RankStars({ value, onChange }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4].map((n) => (
        <button
          key={n}
          onClick={() => onChange(value === n ? n - 1 : n)}
          className={`p-1 transition-colors ${
            n <= value ? "text-[var(--gold)]" : "text-white/15 hover:text-white/40"
          }`}
          data-testid={`builder-rank-${n}`}
          title={`Rank ${n} (+${n * 5}% all stats)`}
        >
          <Star size={18} fill="currentColor" />
        </button>
      ))}
    </div>
  );
}

function StatTile({ Icon, icon, label, value, base, trustDelta = 0, colour, testId }) {
  const Cmp = Icon || icon;
  return (
    <div className="bg-black/30 rounded-md p-2 text-center" data-testid={testId}>
      <Cmp size={14} className={`mx-auto mb-1 ${colour}`} />
      <div className="text-[9px] uppercase tracking-wider text-[var(--muted)]">{label}</div>
      <div className={`text-xl font-mono ${colour}`}>{value}</div>
      <div className="text-[10px] text-[var(--muted)]">base {base}</div>
      {trustDelta > 0 && (
        <div className="text-[10px] text-pink-300 mt-0.5" data-testid={`${testId}-trust`}>
          +{trustDelta} from Trust
        </div>
      )}
    </div>
  );
}

function PartnerSkillBlock({ skill }) {
  if (!skill) return null;
  const name = skill.name || skill.Name;
  const description = skill.description || skill.Description;
  return (
    <div className="border-l-2 border-[var(--teal)]/40 pl-3 mt-4" data-testid="partner-skill">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[var(--muted)] mb-1">
        <Sparkles size={12} className="text-[var(--teal)]" /> Partner Skill
      </div>
      <div className="text-sm font-semibold mb-1">{name}</div>
      {description && <p className="text-[11px] text-[var(--muted)]">{description}</p>}
    </div>
  );
}

// ── Skills panel (active or passive) ──────────────────────────────────
function SkillsPanel({ title, max, selected, setSelected, onAdd, library, kind }) {
  const [search, setSearch] = useState("");
  const [adderOpen, setAdderOpen] = useState(false);

  const remove = (name) => setSelected(selected.filter((s) => s.name !== name));

  const filtered = useMemo(() => {
    const n = search.trim().toLowerCase();
    if (!n) return library.slice(0, 120);
    return library.filter((s) =>
      (s.name || "").toLowerCase().includes(n) ||
      (s.description || "").toLowerCase().includes(n) ||
      (s.asset || "").toLowerCase().includes(n) ||
      (s.element || "").toLowerCase().includes(n)
    ).slice(0, 80);
  }, [library, search]);

  return (
    <div className="glass-card p-4" data-testid={`builder-${kind}-panel`}>
      <header className="flex items-center justify-between mb-3">
        <h3 className="label">{title}</h3>
        <span className="text-[10px] text-[var(--muted)] font-mono">
          {selected.length} / {max}
        </span>
      </header>

      <div className="space-y-2 mb-3">
        {selected.length === 0 && (
          <div className="text-[11px] text-[var(--muted)] italic text-center py-3 border border-dashed border-white/10 rounded-md">
            None equipped
          </div>
        )}
        {selected.map((s) => (
          <SkillBadge key={s.name} skill={s} kind={kind} onRemove={() => remove(s.name)} />
        ))}
      </div>

      {selected.length < max && (
        <>
          {!adderOpen ? (
            <button
              onClick={() => setAdderOpen(true)}
              className="w-full btn btn-ghost text-xs justify-center"
              data-testid={`builder-${kind}-add`}
            >
              <Plus size={12} /> Add {kind === "active" ? "Skill" : "Passive"}
            </button>
          ) : (
            <div className="space-y-2">
              <div className="relative">
                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
                <input
                  autoFocus
                  className="input pl-8 text-sm"
                  placeholder={`Search ${library.length} ${kind === "active" ? "skills" : "passives"}…`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid={`builder-${kind}-search`}
                />
              </div>
              <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
                {filtered.map((s) => {
                  const alreadyPicked = selected.some((x) => x.name === s.name);
                  return (
                    <button
                      key={s.name + (s.asset || "")}
                      disabled={alreadyPicked}
                      onClick={() => {
                        if (alreadyPicked) return;
                        onAdd(s);
                        setSearch("");
                        // Keep the picker open until the user fills the slot —
                        // closing on the first selection broke multi-pick UX.
                        // Close ONLY once the (about-to-be) new selection
                        // would hit the cap.
                        if (selected.length + 1 >= max) setAdderOpen(false);
                      }}
                      className={
                        "w-full text-left px-2 py-1.5 rounded transition-colors flex items-center gap-2 " +
                        (alreadyPicked
                          ? "opacity-40 cursor-not-allowed bg-white/[0.02]"
                          : "hover:bg-white/5")
                      }
                      data-testid={`builder-${kind}-option-${(s.asset || s.name).replace(/[^a-z0-9]/gi, "_")}`}
                    >
                      {kind === "passive" && <PassiveRankBadge rank={s.rank} icon={s.icon} />}
                      {kind === "active" && s.element && (
                        <ElementChip element={s.element} size={12} showLabel={false} />
                      )}
                      <span className="text-xs flex-1 truncate">{s.name}</span>
                      {alreadyPicked && (
                        <span className="text-[9px] text-[var(--muted)] font-mono uppercase">picked</span>
                      )}
                      {!alreadyPicked && kind === "active" && s.power && (
                        <span className="text-[10px] font-mono text-[var(--gold)]">{s.power}</span>
                      )}
                    </button>
                  );
                })}
                {filtered.length === 0 && (
                  <div className="text-[11px] text-[var(--muted)] italic text-center py-2">
                    No matches
                  </div>
                )}
              </div>
              <button
                onClick={() => { setAdderOpen(false); setSearch(""); }}
                className="w-full btn btn-ghost text-[10px] justify-center"
              >
                Cancel
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SkillBadge({ skill, kind, onRemove }) {
  return (
    <div className="group relative bg-black/30 border border-white/5 rounded-md p-2 hover:border-white/20 transition-colors">
      <div className="flex items-start gap-2">
        {kind === "passive" && <PassiveRankBadge rank={skill.rank} icon={skill.icon} />}
        {kind === "active" && skill.element && (
          <ElementChip element={skill.element} size={12} showLabel={false} />
        )}
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium truncate">{skill.name}</div>
          {kind === "active" && (skill.power || skill.cooldown) && (
            <div className="text-[10px] text-[var(--muted)] flex gap-2 mt-0.5">
              {skill.power && <span>PWR <span className="text-[var(--gold)]">{skill.power}</span></span>}
              {skill.cooldown && <span>CD {skill.cooldown}s</span>}
              {skill.type && <span className="uppercase">{skill.type}</span>}
            </div>
          )}
          {kind === "passive" && skill.effects && skill.effects.length > 0 && (
            <div className="text-[10px] text-[var(--muted)] mt-0.5">
              {skill.effects.map((e) => `${e.stat} ${e.value}`).join(" · ")}
            </div>
          )}
          {skill.description && (
            <div className="text-[10px] text-[var(--muted)]/70 mt-1 line-clamp-2">{skill.description}</div>
          )}
        </div>
        <button
          onClick={onRemove}
          className="opacity-30 group-hover:opacity-100 hover:text-red-300 transition-all"
          data-testid={`builder-skill-remove-${(skill.asset || skill.name).replace(/[^a-z0-9]/gi, "_")}`}
          title="Remove"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

function PassiveRankBadge({ rank, icon }) {
  if (rank === null || rank === undefined) {
    return <span className="text-[10px] w-5 h-5 inline-flex items-center justify-center rounded bg-white/5 text-[var(--muted)]">·</span>;
  }
  // Prefer the in-game rank arrow icon if we have it
  if (icon) {
    const url = icon.startsWith("http") ? icon : `${process.env.REACT_APP_BACKEND_URL}${icon}`;
    return (
      <span className="w-5 h-5 inline-flex items-center justify-center" title={`Rank ${rank}`}>
        <img src={url} alt={`Rank ${rank}`} className="w-5 h-5 object-contain" loading="lazy" />
      </span>
    );
  }
  // Fallback to the coloured tier pill
  const pos = rank > 0;
  const colour = !pos
    ? "bg-red-900/60 text-red-300 border-red-500/30"
    : rank === 4
      ? "bg-yellow-900/60 text-yellow-300 border-yellow-400/40"
      : rank === 3
        ? "bg-purple-900/60 text-purple-300 border-purple-400/30"
        : rank === 2
          ? "bg-blue-900/60 text-blue-300 border-blue-400/30"
          : "bg-emerald-900/60 text-emerald-300 border-emerald-400/30";
  return (
    <span className={`text-[10px] w-5 h-5 inline-flex items-center justify-center rounded border font-mono ${colour}`}
          title={`Rank ${rank}`}>
      {pos ? rank : rank}
    </span>
  );
}
